import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractFormDialogData } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';

import { CropField, DetailedCropField, MvcValidationErrorResponse } from 'src/app/shared/models';

import { FailedToSyncCropFields } from '../../models';

export interface SyncCropfieldsFailedDialogData extends AbstractFormDialogData {
  failedCropFields: FailedToSyncCropFields;
}

@Component({
  selector: 'app-sync-cropfields-failed-dialog',
  templateUrl: './sync-cropfields-failed-dialog.component.html',
  styleUrls: ['./sync-cropfields-failed-dialog.component.scss'],
  standalone: false,
})
export class SyncCropfieldsFailedDialogComponent extends AbstractFormDialog<void> {
  data = inject<SyncCropfieldsFailedDialogData>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<SyncCropfieldsFailedDialogComponent>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly locale = inject(LocaleService);

  formGroup: FormGroup;

  constructor() {
    super();
    this.formGroup = this.fb.group({});
  }

  getCropName(cropField: CropField | DetailedCropField): string {
    return 'cropName' in cropField
      ? cropField.cropName
      : this.locale.translate('cropFieldSyncFailedDialog.unknownCrop');
  }

  getLocalisedErrorMessage(error: HttpErrorResponse, batchIndex: number): string {
    if (this.isMvcValidationError(error)) {
      return this.handleMvcValidationError(error.error as MvcValidationErrorResponse, batchIndex);
    }
    if (typeof error.error === 'string') {
      return this.handleStringError(error.error);
    }
    return this.locale.translate('cropFieldSyncFailedDialog.errors.Unknown');
  }

  private isMvcValidationError(error: HttpErrorResponse): boolean {
    return (
      error.status === 400 &&
      error.error !== null &&
      typeof error.error === 'object' &&
      !Array.isArray(error.error)
    );
  }

  private handleMvcValidationError(
    errorBody: MvcValidationErrorResponse,
    batchIndex: number,
  ): string {
    if (errorBody.errors && typeof errorBody.errors === 'object') {
      const validationMessages = this.extractValidationMessages(errorBody.errors, batchIndex);

      if (validationMessages.length > 0) {
        return `${this.locale.translate('cropFieldSyncFailedDialog.errors.ValidationError')}: ${validationMessages.join('; ')}`;
      } else {
        return this.locale.translate('cropFieldSyncFailedDialog.errors.BatchFailed');
      }
    }

    if (errorBody.title) {
      return `${this.locale.translate('cropFieldSyncFailedDialog.errors.ValidationError')}: ${errorBody.title}`;
    }

    return this.locale.translate('cropFieldSyncFailedDialog.errors.Unknown');
  }

  private extractValidationMessages(
    errors: Record<string, string[]>,
    batchIndex: number,
  ): string[] {
    const validationMessages: string[] = [];

    Object.entries(errors).forEach(([field, fieldErrors]) => {
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((fieldError) => {
          const arrayFieldMatch = this.parseArrayFieldName(field);

          // Handle array field like "[0].Name"
          if (arrayFieldMatch && arrayFieldMatch === batchIndex) {
            validationMessages.push(`${fieldError}`);
          }
        });
      }
    });

    return validationMessages;
  }

  private parseArrayFieldName(fieldName: string): number | null {
    const arrayIndexMatch = fieldName.match(/^\[(\d+)\].+$/);

    if (arrayIndexMatch && arrayIndexMatch.length >= 2) {
      const index = parseInt(arrayIndexMatch[1]);

      if (!isNaN(index)) {
        return index;
      }
    }

    return null;
  }

  private handleStringError(errorMessage: string): string {
    if (errorMessage === 'EndDate cannot be before StartDate.') {
      return this.locale.translate('cropFieldSyncFailedDialog.errors.EndDateBeforeStartDate');
    }
    return errorMessage;
  }
}
